import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, CheckCircleSolid, Heading, HStack, List, ListIcon, ListItem, Stack, Text } from '@bonusly/design-system';

import { TrialWhatsNextFreeOption } from '../../../queries/trial-whats-next';
import { FREE_PLAN_PRICE_DISPLAY, TEAM_PLUS_BIZY_AI_MONTHLY_PRICE_DISPLAY } from '../lib/plan-pricing';

interface PlanCardShellProps {
	title: string;
	price: string;
	priceSuffix?: string;
	recommendedLabel?: string;
	bullets: string[];
	children: React.ReactNode;
	isDisabled?: boolean;
	testId: string;
	// Tightens padding and bullet spacing for the interstitial, which must fit
	// the viewport. The standalone route renders the roomier default.
	compact?: boolean;
}

// Shared card chrome so the Team and Free cards stay visually consistent. The
// recommended card gets the brand-violet accent border (`text.hashtag`, the
// same purple the app uses for the hashtag/brand accent) and a solid-purple
// "RECOMMENDED" badge seated on its top edge; the Free card keeps the neutral
// light-gray border and no badge. The disabled card dims its body but keeps the
// disabled reason fully legible.
const PlanCardShell: FC<PlanCardShellProps> = ({ title, price, priceSuffix, recommendedLabel, bullets, children, isDisabled, testId, compact = false }) => (
	<Stack
		bgColor="bg.surface"
		borderColor={recommendedLabel ? 'text.hashtag' : 'border.default'}
		borderRadius="lg"
		borderWidth={recommendedLabel ? '2px' : '1px'}
		flex={1}
		gap={compact ? 3 : 4}
		p={compact ? 5 : 6}
		position="relative"
		data-testid={testId}
	>
		{recommendedLabel && (
			<Text
				position="absolute"
				top={0}
				left={6}
				transform="translateY(-50%)"
				bgColor="text.hashtag"
				color="text.onColor"
				borderRadius="full"
				px={3}
				py={1}
				fontSize="xs"
				fontWeight="bold"
				letterSpacing="wider"
				textTransform="uppercase"
				data-testid={`${testId}-recommended-badge`}
			>
				{recommendedLabel}
			</Text>
		)}

		<Box opacity={isDisabled ? 0.6 : 1}>
			<Heading size="md" color="text.default">
				{title}
			</Heading>
			<HStack align="baseline" mt={2} spacing={1}>
				<Text color="text.default" fontSize="2xl" fontWeight="bold">
					{price}
				</Text>
				{priceSuffix && (
					<Text color="text.softer" fontSize="sm">
						{priceSuffix}
					</Text>
				)}
			</HStack>

			<List mt={compact ? 3 : 4} spacing={compact ? 1 : 2}>
				{bullets.map((bullet) => (
					<ListItem key={bullet} display="flex" alignItems="flex-start">
						<ListIcon as={CheckCircleSolid} color="success.icon" mt={1} />
						<Text color="text.default" fontSize="sm">
							{bullet}
						</Text>
					</ListItem>
				))}
			</List>
		</Box>

		<Box mt="auto">{children}</Box>
	</Stack>
);

interface PlanCardsProps {
	freeOption: TrialWhatsNextFreeOption;
	onContinueOnTeam: () => void;
	onSwitchToFree: () => void;
	compact?: boolean;
}

export const PlanCards: FC<PlanCardsProps> = ({ freeOption, onContinueOnTeam, onSwitchToFree, compact = false }) => {
	const { t } = useTranslation('trial_whats_next', { keyPrefix: 'plans' });

	// Display name and bullets mirror the public pricing page via i18n.
	const teamName = t('team.name');

	// Bullets are kept in i18n (returnObjects) so copy can match the public
	// pricing page without a code change.
	const teamBullets = t('team.bullets', { returnObjects: true }) as string[];
	const freeBullets = t('free.bullets', { returnObjects: true }) as string[];

	const isFreeDisabled = !freeOption.eligible;

	return (
		<HStack align="stretch" gap={compact ? 4 : 6} flexWrap={{ base: 'wrap', md: 'nowrap' }}>
			<PlanCardShell
				title={teamName}
				price={TEAM_PLUS_BIZY_AI_MONTHLY_PRICE_DISPLAY}
				priceSuffix={t('team.price_suffix')}
				recommendedLabel={t('team.recommended')}
				bullets={teamBullets}
				testId="trial-whats-next-team-card"
				compact={compact}
			>
				<Button colorScheme="primary" width="100%" onClick={onContinueOnTeam} data-testid="trial-whats-next-continue-on-team">
					{t('team.cta')}
				</Button>
			</PlanCardShell>

			<PlanCardShell
				title={t('free.name')}
				price={FREE_PLAN_PRICE_DISPLAY}
				priceSuffix={t('free.price_suffix')}
				bullets={freeBullets}
				isDisabled={isFreeDisabled}
				testId="trial-whats-next-free-card"
				compact={compact}
			>
				{isFreeDisabled ? (
					<Stack gap={2}>
						<Button variant="outline" width="100%" isDisabled data-testid="trial-whats-next-switch-to-free">
							{t('free.cta')}
						</Button>
						{freeOption.reason && (
							<Text color="text.softer" fontSize="sm" data-testid="trial-whats-next-free-disabled-reason">
								{freeOption.reason}
							</Text>
						)}
					</Stack>
				) : (
					<Button variant="outline" width="100%" onClick={onSwitchToFree} data-testid="trial-whats-next-switch-to-free">
						{t('free.cta')}
					</Button>
				)}
			</PlanCardShell>
		</HStack>
	);
};
